
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from 'sonner';
import ImageUploader from '@/components/common/ImageUploader';
import GalleryImageGrid, { GalleryImage } from './GalleryImageGrid';
import { supabase } from "@/integrations/supabase/client";

interface ImageSelectorProps {
  onUploadComplete: (imageUrl: string) => void;
  onGalleryImageSelect: (image: GalleryImage) => void;
  defaultCategory?: "finance" | "housing" | "loans" | "lifestyle" | "energy" | "car" | "boats" | "guide" | "logo";
  useStorageBucket?: boolean;
}

const ImageSelector: React.FC<ImageSelectorProps> = ({ 
  onUploadComplete, 
  onGalleryImageSelect,
  defaultCategory = "finance",
  useStorageBucket = true
}) => {
  const [activeTab, setActiveTab] = useState<string>("gallery");
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>(defaultCategory);
  
  const categories = [
    { id: "finance", name: "Talous" },
    { id: "housing", name: "Asuminen" },
    { id: "loans", name: "Lainat" },
    { id: "lifestyle", name: "Elämäntyyli" },
    { id: "energy", name: "Energia" },
    { id: "car", name: "Autot" },
    { id: "boats", name: "Veneet" },
    { id: "guide", name: "Oppaat" },
    { id: "logo", name: "Logot" }
  ];

  useEffect(() => {
    fetchImages(selectedCategory);
  }, [selectedCategory]);

  const fetchImages = async (category: string) => {
    setLoading(true);
    
    try {
      let imageList: GalleryImage[] = [];
      
      if (useStorageBucket) {
        // Fetch from Supabase storage bucket
        const { data, error } = await supabase.storage
          .from('article-images')
          .list(category, {
            sortBy: { column: 'created_at', order: 'desc' }
          });
        
        if (error) {
          console.error("Supabase fetch error:", error);
          throw error;
        }
        
        if (data && data.length > 0) {
          imageList = data
            .filter(item => !item.id.endsWith('/') && !item.name.endsWith('/'))
            .map(item => {
              const { data } = supabase.storage
                .from('article-images')
                .getPublicUrl(`${category}/${item.name}`);
                
              return {
                id: item.id,
                src: data.publicUrl,
                alt: item.name,
                category,
                publicUrl: data.publicUrl
              };
            });
        } else {
          console.log(`No images found in category: ${category}`);
        }
      } else {
        // Use the predefined gallery images
        // This is left as a fallback
        const galleryImages = require('@/components/common/galleryImages.ts').default;
        imageList = galleryImages
          .filter(img => img.category === category)
          .map(img => ({
            id: img.id,
            src: img.src,
            alt: img.alt,
            category: img.category,
            publicUrl: img.src // For local images, the publicUrl is the same as src
          }));
      }
      
      setImages(imageList);
      console.log(`Loaded ${imageList.length} images for category ${category}`);
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error("Kuvien lataaminen epäonnistui");
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const handleRefresh = () => {
    fetchImages(selectedCategory);
    toast.success("Kuvat päivitetty");
  };

  // Function to copy image public URL to clipboard
  const copyImageUrlToClipboard = (image: GalleryImage) => {
    if (image.publicUrl) {
      navigator.clipboard.writeText(image.publicUrl);
      toast.success("Kuvan URL kopioitu leikepöydälle!");
    }
  };

  return (
    <div className="w-full">
      <h2 className="text-xl font-bold mb-4">Valitse kuva</h2>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 mb-4">
          <TabsTrigger value="gallery">Kuvapankki</TabsTrigger>
          <TabsTrigger value="upload">Lataa uusi kuva</TabsTrigger>
        </TabsList>
        
        <TabsContent value="gallery" className="space-y-4">
          <div className="flex flex-wrap justify-between mb-4">
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleCategoryChange(category.id)}
                >
                  {category.name}
                </Button>
              ))}
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              className="ml-auto"
            >
              Päivitä kuvat
            </Button>
          </div>
          
          <GalleryImageGrid
            images={images}
            onSelect={onGalleryImageSelect}
            loading={loading}
            onCopyUrl={copyImageUrlToClipboard}
          />
        </TabsContent>
        
        <TabsContent value="upload">
          <ImageUploader 
            onUploadComplete={(url) => {
              onUploadComplete(url);
              // After upload, switch back to gallery and refresh
              setActiveTab("gallery");
              setTimeout(() => fetchImages(selectedCategory), 1000);
            }} 
            category={selectedCategory as any}
            useStorageBucket={useStorageBucket}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ImageSelector;
